import { Injectable, OnDestroy } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {
	Aufgabe,
	AufgabeFacade,
	AufgabeLfprKostenfreiGrund,
	AufgabeLfprKostenTaetigkeit,
	AufgabeLfprKostenTraeger,
	AufgabenTyp,
	AufgabeRechnungsstellungErledigung,
	AufgabeVersandTaetigkeit,
	AufgabeZertifikatTaetigkeit,
} from '@schir-int-client/aufgabe-shared';
import { isNil } from 'lodash-es';
import { Subscription } from 'rxjs';
import { KontaktKategorie } from '@schir-int-client/adressverwaltung-shared';

@Injectable({ providedIn: 'root' })
export class CreateAufgabeFormService implements OnDestroy {
	static readonly integerValidator = Validators.pattern('^\\d+$');

	readonly form = createForm();

	private aufgabeSubscription: Subscription;
	private mitteilungSubscription: Subscription;

	constructor(private facade: AufgabeFacade) {
		this.disableAllTypeGroups();
		this.form.controls.typ.valueChanges.subscribe(selectedType => {
			if (selectedType != null) {
				this.disableAllTypeGroups();
				this.enableAufgabenTyp(selectedType);
			}
		});
	}

	private changeEnabledState(control: AbstractControl, enable: boolean) {
		if (enable) {
			control.enable();
		} else {
			control.disable();
		}
	}

	public disableAllTypeGroups(): void {
		Object.keys(AufgabenTyp)
			.map(aufgabeTyp => AufgabenTyp[aufgabeTyp])
			.forEach(aufgabeTyp => this.disableAufgabenTyp(aufgabeTyp));
	}

	submit(): boolean {
		if (this.form.valid) {
			if (this.aufgabeSubscription) {
				this.facade.updateAufgabe(this.buildAufgabeByTyp(this.form.value.typ));
			} else {
				Object.values(AufgabenTyp).forEach(aufgabenTyp => {
					if (!isNil(this.form.value[aufgabenTyp]) || this.form.value.typ == aufgabenTyp) {
						this.facade.createAufgabe(this.buildAufgabeByTyp(aufgabenTyp));
					}
				});
			}
			return true;
		}
	}

	isValid(): boolean {
		return this.form.valid;
	}

	public buildAufgabeByTyp(typ: AufgabenTyp): Aufgabe {
		const formData = this.form.value;
		if (!isNil(formData[typ])) {
			let notiz = formData[typ].notiz;
			if (isNil(notiz) && (typ == AufgabenTyp.FINANZSANKTIONSLISTE || typ == AufgabenTyp.ALTER_SCHIFFSBRIEF)) {
				notiz = 'kein Eintrag vorhanden';
			}
			let result: Aufgabe = <any>{ ...formData[typ], notiz: notiz, typ };
			result = { ...result, body: isNil(formData[typ]['body']) ? null : formData[typ]['body'] };
			delete result[typ];
			return result;
		} else {
			return <Aufgabe>{ typ, body: null };
		}
	}

	subscribeToAufgabe() {
		this.aufgabeSubscription = this.facade.aufgabe.subscribe(aufgabe => {
			if (aufgabe) {
				this.form.patchValue(this.prepareForPatch(aufgabe));
				this.disableAllTypeGroups();
				this.enableAufgabenTyp(aufgabe.typ);
			}
		});
	}

	prepareForPatch(aufgabe: Aufgabe): object {
		const body = this.removeNullValues(aufgabe.body);
		return { typ: aufgabe.typ, [aufgabe.typ]: { body: body, notiz: aufgabe.notiz } };
	}

	private removeNullValues(body: object): object {
		const result = { ...body };
		Object.getOwnPropertyNames(result)
			.filter(prop => isNil(result[prop]))
			.forEach(prop => delete result[prop]);
		return result;
	}

	public enableAufgabenTyp(aufgabenTyp: AufgabenTyp) {
		this.form.controls[aufgabenTyp].enable();
		if (
			aufgabenTyp === AufgabenTyp.LASTENFREIHEITSBESCHEINIGUNG ||
			aufgabenTyp === AufgabenTyp.LOESCHUNGSBESCHEINIGUNG ||
			aufgabenTyp === AufgabenTyp.HYPOTHEKENBESCHEINIGUNG ||
			aufgabenTyp === AufgabenTyp.EIGENTUEMERBESCHEINIGUNG ||
			aufgabenTyp === AufgabenTyp.NAMENSAENDERUNGSBESCHEINIGUNG
		) {
			const bcs = this.form.controls[aufgabenTyp].controls.body.controls;
			this.changeEnabledState(bcs.faxEmpfaenger, bcs.vorabPerFax.value);
		} else if (aufgabenTyp === AufgabenTyp.ZERTIFIKATSAUSZUG) {
			const bcs = this.form.controls[aufgabenTyp].controls.body.controls;
			this.changeEnabledState(
				bcs.taetigkeit,
				bcs.zertifikatTaetigkeit.value !== AufgabeZertifikatTaetigkeit.NOTIZ
			);
			this.changeEnabledState(
				bcs.empfaenger,
				bcs.zertifikatTaetigkeit.value !== AufgabeZertifikatTaetigkeit.NOTIZ
			);
			this.changeEnabledState(
				bcs.vorabPerFax,
				bcs.zertifikatTaetigkeit.value !== AufgabeZertifikatTaetigkeit.NOTIZ
			);
			this.changeEnabledState(
				bcs.faxEmpfaenger,
				bcs.zertifikatTaetigkeit.value !== AufgabeZertifikatTaetigkeit.NOTIZ && bcs.vorabPerFax.value
			);
		} else if (aufgabenTyp === AufgabenTyp.EINTRAGUNGSMITTEILUNG) {
			const bcs = this.form.controls[aufgabenTyp].controls.body.controls;
			this.changeEnabledState(bcs.eigentuemerNeuMitteilung, bcs.anEigentuemerNeuSenden.value);
			this.changeEnabledState(bcs.eigentuemerAltMitteilung, bcs.anEigentuemerAltSenden.value);
			this.changeEnabledState(bcs.eingetragenerEigentuemerMitteilung, bcs.anEingetragenerEigentuemerSenden.value);
			this.changeEnabledState(bcs.glaeubigerNeuMitteilung, bcs.anGlaeubigerNeuSenden.value);
			this.changeEnabledState(bcs.glaeubigerAltMitteilung, bcs.anGlaeubigerAltSenden.value);
			this.changeEnabledState(bcs.notarMitteilung, bcs.anNotarSenden.value);
			this.changeEnabledState(bcs.bshMitteilung, bcs.anBshSenden.value);
			this.changeEnabledState(bcs.rechtsanwaelteMitteilung, bcs.anRechtsanwaelteSenden.value);
		} else if (aufgabenTyp === AufgabenTyp.MESSBRIEF || aufgabenTyp === AufgabenTyp.VERMESSUNGSURKUNDE) {
			const bcs = this.form.controls[aufgabenTyp].controls.body.controls;
			this.changeEnabledState(bcs.originalAn, bcs.original.value);
			this.changeEnabledState(bcs.ergaenzenSenden, bcs.ergaenzen.value);
		} else if (aufgabenTyp === AufgabenTyp.HEIMATHAFENVERLEGUNG || aufgabenTyp === AufgabenTyp.HEIMATORTVERLEGUNG) {
			const bcs = this.form.controls[aufgabenTyp].controls.body.controls;
			this.changeEnabledState(bcs.gericht, bcs.anGerichtSenden.value);
		} else if (
			aufgabenTyp === AufgabenTyp.ZERTIFIKAT ||
			aufgabenTyp === AufgabenTyp.SCHIFFSZERTIFIKAT ||
			aufgabenTyp === AufgabenTyp.SCHIFFSBRIEF
		) {
			const bcs = this.form.controls[aufgabenTyp].controls.body.controls;
			this.changeEnabledState(bcs.sendenAn, bcs.taetigkeit.value !== AufgabeZertifikatTaetigkeit.NOTIZ);
			this.changeEnabledState(bcs.versandTaetigkeit, bcs.taetigkeit.value !== AufgabeZertifikatTaetigkeit.NOTIZ);
		}
	}

	public disableAufgabenTyp(aufgabenTyp: AufgabenTyp) {
		if (aufgabenTyp === AufgabenTyp.EINTRAGUNGSMITTEILUNG) {
			this.mitteilungSubscription?.unsubscribe();
			this.mitteilungSubscription = null;
		}
		this.form.controls[aufgabenTyp].disable();
	}

	ngOnDestroy(): void {
		this.aufgabeSubscription?.unsubscribe();
		this.mitteilungSubscription?.unsubscribe();
	}
}

export function createForm() {
	return new FormGroup({
		typ: new FormControl<AufgabenTyp>(null),
		[AufgabenTyp.FINANZSANKTIONSLISTE]: createNotizFormGroup(),
		[AufgabenTyp.ZERTIFIKAT]: createNotizWithBodyFormGroup(createZertifikatFromGroup),
		[AufgabenTyp.SCHIFFSBRIEF]: createNotizWithBodyFormGroup(createZertifikatFromGroup),
		[AufgabenTyp.ALTER_SCHIFFSBRIEF]: createNotizFormGroup(),
		[AufgabenTyp.SCHIFFSZERTIFIKAT]: createNotizWithBodyFormGroup(createZertifikatFromGroup),
		[AufgabenTyp.ALTES_ZERTIFIKAT]: createNotizFormGroup(),
		[AufgabenTyp.ZERTIFIKATSAUSZUG]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					zertifikatTaetigkeit: new FormControl(AufgabeZertifikatTaetigkeit.FERTIGEN, Validators.required),
					taetigkeit: new FormControl(AufgabeVersandTaetigkeit.SENDEN_AN, Validators.required),
					empfaenger: new FormControl<KontaktKategorie[]>(null, Validators.required),
					vorabPerFax: new FormControl<boolean>(null),
					faxEmpfaenger: new FormControl<KontaktKategorie[]>(null, Validators.required),
				})
		),
		[AufgabenTyp.ABSCHRIFT_ZERTIFIKATSAUSZUG]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					taetigkeit: new FormControl(AufgabeVersandTaetigkeit.SENDEN_AN, Validators.required),
					empfaenger: new FormControl<KontaktKategorie[]>(null, Validators.required),
				})
		),
		[AufgabenTyp.ALTER_AUSZUG]: createNotizFormGroup(),
		[AufgabenTyp.HYPOTHEKENBESCHEINIGUNG]: createNotizWithBodyFormGroup(createVersandWithFaxBodyFormGroup),
		[AufgabenTyp.EIGENTUEMERBESCHEINIGUNG]: createNotizWithBodyFormGroup(createVersandWithFaxBodyFormGroup),
		[AufgabenTyp.LOESCHUNGSBESCHEINIGUNG]: createNotizWithBodyFormGroup(createVersandWithFaxBodyFormGroup),
		[AufgabenTyp.LASTENFREIHEITSBESCHEINIGUNG]: createNotizWithBodyFormGroup(createVersandWithFaxBodyFormGroup),
		[AufgabenTyp.NAMENSAENDERUNGSBESCHEINIGUNG]: createNotizWithBodyFormGroup(createVersandWithFaxBodyFormGroup),
		[AufgabenTyp.REGISTERABSCHRIFT]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					beglaubigte: new FormControl<number | null>(null, CreateAufgabeFormService.integerValidator),
					einfache: new FormControl<number | null>(null, CreateAufgabeFormService.integerValidator),
				})
		),
		[AufgabenTyp.EINTRAGUNGSMITTEILUNG]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					anEigentuemerNeuSenden: new UntypedFormControl(null),
					eigentuemerNeuMitteilung: new FormGroup({
						blattabschrift: new UntypedFormControl(null),
						zertifikatsBerichtigung: new UntypedFormControl(null),
						auszugsEinreichung: new UntypedFormControl(null),
					}),
					anEigentuemerAltSenden: new UntypedFormControl(null),
					eigentuemerAltMitteilung: new FormGroup({
						blattabschrift: new UntypedFormControl(null),
						zertifikatsBerichtigung: new UntypedFormControl(null),
						auszugsEinreichung: new UntypedFormControl(null),
					}),
					anEingetragenerEigentuemerSenden: new UntypedFormControl(null),
					eingetragenerEigentuemerMitteilung: new FormGroup({
						blattabschrift: new UntypedFormControl(null),
						zertifikatsBerichtigung: new UntypedFormControl(null),
						auszugsEinreichung: new UntypedFormControl(null),
					}),
					anGlaeubigerNeuSenden: new UntypedFormControl(null),
					glaeubigerNeuMitteilung: new FormGroup({
						blattabschrift: new UntypedFormControl(null),
						zertifikatsBerichtigung: new UntypedFormControl(null),
					}),

					anGlaeubigerAltSenden: new UntypedFormControl(null),
					glaeubigerAltMitteilung: new FormGroup({
						blattabschrift: new UntypedFormControl(null),
						zertifikatsBerichtigung: new UntypedFormControl(null),
					}),

					anNotarSenden: new UntypedFormControl(null),
					notarMitteilung: new FormGroup({ blattabschrift: new UntypedFormControl(null) }),

					anBehoerdenSenden: new UntypedFormControl(null),

					anBshSenden: new UntypedFormControl(null),
					bshMitteilung: new FormGroup({ blattabschrift: new UntypedFormControl(null) }),

					anRechtsanwaelteSenden: new UntypedFormControl(null),
					rechtsanwaelteMitteilung: new FormGroup({
						blattabschrift: new UntypedFormControl(null),
						zertifikatsBerichtigung: new UntypedFormControl(null),
					}),
				})
		),
		[AufgabenTyp.MESSBRIEF]: createMessbriefFormGroup(),
		[AufgabenTyp.VERMESSUNGSURKUNDE]: createMessbriefFormGroup(),
		[AufgabenTyp.SONSTIGE]: createNotizFormGroup(),
		[AufgabenTyp.RECHNUNGSSTELLUNG]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					erledigung: new FormControl(AufgabeRechnungsstellungErledigung.ERLEDIGT, Validators.required),
				})
		),
		[AufgabenTyp.KOSTEN]: createNotizFormGroup(),
		[AufgabenTyp.STATISTIK]: createNotizFormGroup(),
		[AufgabenTyp.HEIMATHAFENVERLEGUNG]: createNotizWithBodyFormGroup(createHeimatverlegungFormGroup),
		[AufgabenTyp.HEIMATORTVERLEGUNG]: createNotizWithBodyFormGroup(createHeimatverlegungFormGroup),
		[AufgabenTyp.WIEDERVORLAGE]: createNotizWithBodyFormGroup(
			() => new FormGroup({ wiedervorlageDatum: new FormControl(createDefaultDate()) })
		),
		[AufgabenTyp.ABLAGE]: createNotizFormGroup(),
		[AufgabenTyp.ANSCHREIBEN]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					vorlageId: new FormControl<number>(null),
					vorlageIds: new FormArray([new FormControl<number>(null)]),
					datum: new FormControl(new Date()),
					rechtspfleger: new FormControl<string>(null),
					taetigkeit: new FormControl(AufgabeVersandTaetigkeit.SENDEN_AN),
					empfaenger: new FormControl<KontaktKategorie[]>(null),
				})
		),
		[AufgabenTyp.LFPR_REGISTERAKTE_ANLEGEN]: createNotizFormGroup(),
		[AufgabenTyp.LFPR_SCHULDTITEL]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					eintragung: new FormControl<boolean>(null),
					abschrift: new FormControl<boolean>(null),
					vollstreckungsunterlagen: new FormControl<boolean>(null),
				})
		),
		[AufgabenTyp.LFPR_MITTEILUNG]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					anEigentuemerNeu: new FormControl<boolean>(null),
					eigentuemerNeu: createLfprMitteilungEigentuemerFormGroup(),
					anEigentuemerAlt: new FormControl<boolean>(null),
					eigentuemerAlt: createLfprMitteilungEigentuemerFormGroup(),
					anNotar: new FormControl<boolean>(null),
					notar: new FormGroup({ uvzNr: new FormControl<string>(null) }),
					anLuftfahrtBundesamt: new FormControl<boolean>(null),
					luftfahrtBundesAmt: new FormGroup({ nrLuftfahrzeugrolle: new FormControl<string>(null) }),
					anGlaeubigerNeu: new FormControl<boolean>(null),
					glaeubigerNeu: createLfprMitteilungGlaeubigerFormGroup(),
					anGlaeubigerAlt: new FormControl<boolean>(null),
					glaeubigerAlt: createLfprMitteilungGlaeubigerFormGroup(),
					anBerechtigter: new FormControl<boolean>(null),
					berechtigterNr: new FormControl<string>(null),
					berechtigter: new FormGroup({
						ueberAnwalt: new FormControl<boolean>(null),
						ueberNotar: new FormControl<boolean>(null),
					}),
					anRechtsanwalt: new UntypedFormControl(null),
					rechtsanwalt: new FormGroup({ zuAz: new FormControl<string>(null) }),
				})
		),
		[AufgabenTyp.LFPR_STATISTIK]: createNotizFormGroup(),
		[AufgabenTyp.LFPR_VERWEISUNGSVERMERK]: createNotizFormGroup(),
		[AufgabenTyp.LFPR_DB_AKTUALISIEREN]: createNotizFormGroup(),
		[AufgabenTyp.LFPR_BLATTABSCHRIFT]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					einfacheAn: new FormControl<number>(null),
					einfache: createLfprAbschriftFormGroup(),
					beglaubigteAn: new FormControl<number>(null),
					beglaubigte: createLfprAbschriftFormGroup(),
				})
		),
		[AufgabenTyp.LFPR_NEGATIVBESCHEINIGUNG]: createNotizFormGroup(),
		[AufgabenTyp.LFPR_KOSTEN]: createNotizWithBodyFormGroup(
			() =>
				new FormGroup({
					taetigkeit: new FormControl<AufgabeLfprKostenTaetigkeit>(null, Validators.required),
					traeger: new FormControl<AufgabeLfprKostenTraeger>(null),
					berechtigteNr: new FormControl<string | null>(null),
					grund: new FormControl<AufgabeLfprKostenfreiGrund>(null),
				})
		),
		[AufgabenTyp.LFPR_WIEDERVORLAGE]: createNotizWithBodyFormGroup(
			() => new FormGroup({ wiedervorlageDatum: new FormControl(createDefaultDate()) })
		),
		[AufgabenTyp.LFPR_ERLEDIGT]: createNotizFormGroup(),
		[AufgabenTyp.LFPR_WEGLEGEN]: createNotizWithBodyFormGroup(
			() => new FormGroup({ aufbewahrenBis: new FormControl(createDefaultAufbewahrenDate(30)) })
		),
		[AufgabenTyp.LFPR_SAMMLUNG]: createNotizWithBodyFormGroup(
			() => new FormGroup({ aufbewahrenBis: new FormControl(createDefaultAufbewahrenDate(5)) })
		),
	});
}

function createNotizWithBodyFormGroup<T extends FormGroup>(bodyBuilder: () => T) {
	return new FormGroup({ notiz: new FormControl<string | null>(null), body: bodyBuilder() });
}

function createHeimatverlegungFormGroup() {
	return new FormGroup({
		retentAnlegen: new FormControl(false),
		anGerichtSenden: new FormControl(false),
		gericht: new UntypedFormControl(null, Validators.required),
		umEintragungsnachrichtBitten: new FormControl(false),
	});
}

function createVersandWithFaxBodyFormGroup() {
	return new FormGroup({
		taetigkeit: new FormControl(AufgabeVersandTaetigkeit.SENDEN_AN, Validators.required),
		empfaenger: new FormControl<KontaktKategorie[] | null>(null, Validators.required),
		vorabPerFax: new FormControl(false),
		faxEmpfaenger: new FormControl<KontaktKategorie[] | null>(null, Validators.required),
	});
}

function createNotizFormGroup() {
	return new FormGroup({ notiz: new FormControl<string | null>(null) });
}

function createLfprAbschriftFormGroup() {
	return new FormGroup({
		glaeubigerNeu: new FormControl<boolean>(null),
		glaeubigerNeuNr: new FormControl<string>(null),
		glaeubigerAlt: new FormControl<boolean>(null),
		glaeubigerAltNr: new FormControl<string>(null),
		notar: new FormControl<boolean>(null),
		eigentuemerNeu: new FormControl<boolean>(null),
		eigentuemerAlt: new FormControl<boolean>(null),
		rechtsanwalt: new FormControl<boolean>(null),
	});
}

function createLfprMitteilungEigentuemerFormGroup() {
	return new FormGroup({ ueberAnwalt: new FormControl<boolean>(null), ueberNotar: new FormControl<boolean>(null) });
}

function createLfprMitteilungGlaeubigerFormGroup() {
	return new FormGroup({ ruecksendung: new UntypedFormControl(null) });
}

function createMessbriefFormGroup() {
	return createNotizWithBodyFormGroup(
		() =>
			new FormGroup({
				mitUsigVersehen: new FormControl<boolean>(null),
				mitVermerkVersehen: new FormControl<boolean>(null),
				original: new FormControl<boolean>(null),
				originalAn: new FormGroup({
					eigentuemer: new FormControl<boolean>(null),
					vertreter: new FormControl<boolean>(null),
				}),
				vorabKopie: new FormControl<boolean>(null),
				zda: new FormControl<boolean>(null),
				ergaenzen: new FormControl<boolean>(null),
				ergaenzenSenden: new FormGroup({
					bsh: new FormControl<boolean>(null),
					gdwsMainz: new FormControl<boolean>(null),
					einreicher: new FormControl<boolean>(null),
				}),
			})
	);
}

function createZertifikatFromGroup() {
	return new FormGroup({
		taetigkeit: new FormControl(AufgabeZertifikatTaetigkeit.FERTIGEN, Validators.required),
		versandTaetigkeit: new FormControl(AufgabeVersandTaetigkeit.SENDEN_AN, Validators.required),
		sendenAn: new FormControl<KontaktKategorie[]>(null, Validators.required),
	});
}

function createDefaultDate(): Date {
	const date = new Date();
	date.setDate(new Date().getDate() + 8 * 7);
	return date;
}

function createDefaultAufbewahrenDate(fullYears: number): Date {
	const date = new Date();
	date.setFullYear(date.getFullYear() + fullYears, 11, 31);
	return date;
}
